import React, { useContext } from 'react';

import formatSeconds from '$/utils/formatSeconds';
import { css } from '@emotion/react';
import { AnimatePresence, motion, useTransform } from 'framer-motion';
import { withWebinarPlayerContext } from '../context';
import { t } from '@lingui/macro';
import { InteractionInfo } from '$/components/Interactions/InteractionInfo';
import { FormOptionsContext } from '$/components/ui/Form';
import { AttendeeCardForm } from '$/components/Interactions/cards/AttendeeCardForm';
import CardMain from '$/components/Interactions/cards/CardMain';
import { readableColorOnBackground } from '$/shared/frontend/utils/tinyColor';
import { FilledMarkdown } from '$/components/ui/EditableInput/DangerouslySetInnerHTML';
import { PlayerMode } from '../types';
import useRunOnce from '$/hooks/useRunOnce';

export const PinnedCard: React.FC<{ interaction: InteractionInfo }> = withWebinarPlayerContext(
  [
    'ewebinar',
    'mode',
    'playbackPositionValue',
    'interactionInEdit',
    'dimensions',
    'currentRoom',
    'scrollToCardOrPosition',
    'isCollapsedEmbeddedPlayer',
    'transitions',
    'showingInVideoFormBeforeWatching',
  ],
  'PinnedCard'
)(({ interaction, context }) => {
  const { onSubmit } = useContext(FormOptionsContext);

  const {
    ewebinar,
    mode,
    playbackPositionValue,
    interactionInEdit,
    dimensions,
    currentRoom,
    scrollToCardOrPosition,
    isCollapsedEmbeddedPlayer,
    transitions,
    showingInVideoFormBeforeWatching,
  } = context;
  const endsIn = interaction.details?.endsIn;
  const readableColorOnPrimary = readableColorOnBackground(ewebinar.primaryColor);

  const hasTimer = interaction.showTimer(mode);
  const refTimeDisplay = React.useRef<HTMLDivElement>(null);

  useTransform(playbackPositionValue, (pos) => {
    const node = refTimeDisplay.current;
    if (!node || !endsIn) return;

    const at = interaction.playbackPosition;
    if (pos < at) return;

    if (pos > at + endsIn) {
      node.innerText = t`Expired`;
      return;
    }

    node.innerText = formatSeconds(
      interactionInEdit?.id === interaction.id ? endsIn : interaction.timeLeft(pos),
      true
    );
  });

  const onButtonClick = React.useCallback(() => {
    if (interaction.isSingleButtonCard()) {
      onSubmit();
      return;
    }

    scrollToCardOrPosition(interaction);
  }, [interaction, onSubmit, scrollToCardOrPosition]);

  const [zIndex, setZIndex] = React.useState(405);
  useRunOnce(() => {
    setTimeout(() => {
      setZIndex(420);
    }, 1500);
  });
  const { fontSize, isSmall, height, xPx, yPx, videoYpx, animateRadius } = React.useMemo(() => {
    const margin =
      (dimensions.videoControlsArea.topBar.height - dimensions.actionsPane.pinnedCard.height) / 2;
    const xPx = (dimensions.videoArea.width - dimensions.videoStage.unscaled.width) / 2;
    let yPx = currentRoom.caps.hasVideoPlayer
      ? (dimensions.videoArea.height - dimensions.videoStage.unscaled.height) / 2 - margin
      : margin;

    const scaleRatio = dimensions.videoStage.unscaled.width / 500;

    let height = Math.min(
      dimensions.actionsPane.pinnedCard.height,
      dimensions.actionsPane.pinnedCard.height * scaleRatio
    );
    if (dimensions.actionsPane.location === 'bottom') {
      // We should maintain the original height of the pinned card when in portrait mode
      // as there's no space between the pinned card and the video player
      height = dimensions.actionsPane.pinnedCard.height;
    }

    const videoYpx =
      dimensions.videoControlsArea.topBar.height +
      (dimensions.videoArea.height - dimensions.videoStage.unscaled.height) / 2;

    yPx =
      currentRoom.caps.hasVideoPlayer && !currentRoom.caps.hideVideoPlayerStage
        ? Math.round(videoYpx - height)
        : yPx;

    if (isCollapsedEmbeddedPlayer) {
      yPx = -dimensions.actionsPane.pinnedCard.height;
    }

    const fontSizeTransform = scaleRatio * 17;
    const fontSize = Math.min(17, Math.max(9.5, fontSizeTransform < 0 ? 100 : fontSizeTransform));

    const isSmall = dimensions.videoStage.unscaled.width < 480;

    const radius = dimensions.actionsPane.pinnedCard.radius;
    let animateRadius = `${dimensions.actionsPane.pinnedCard.radius}px`;
    if (currentRoom.caps.hasVideoPlayer && !currentRoom.caps.hideVideoPlayerStage) {
      animateRadius = `${radius}px ${radius}px 0 0`;
    }

    return {
      fontSize,
      isSmall,
      height,
      xPx,
      yPx,
      videoYpx,
      animateRadius,
    };
  }, [dimensions, currentRoom, isCollapsedEmbeddedPlayer]);

  return (
    <motion.div
      id='PinnedCard'
      className={`
        PinnedCard room-font font-semibold flex flex-row justify-between items-center
      `}
      css={css`
        position: absolute;
        top: 0;
        left: 0;
        transition: opacity 0.5s;
        transform-origin: bottom left;
        padding: ${isSmall ? '0.25rem 0.5rem' : '0.25rem 1.5rem'};
        z-index: ${zIndex};

        pointer-events: none;
        background: ${ewebinar.primaryColor};
        ${showingInVideoFormBeforeWatching && mode !== PlayerMode.edit ? 'visibility: hidden;' : ''}

        button {
          font-size: ${fontSize * 0.9}px;
          max-height: ${height * 0.8}px;
        }

        > * {
          pointer-events: all;
        }
      `}
      transition={transitions['video.resize']}
      initial={{
        opacity: '1',
        y: `${videoYpx}px`,
        x: `${xPx}px`,
        height: `${height}px`,
        width: `${dimensions.videoStage.unscaled.width}px`,
        borderRadius: `${dimensions.actionsPane.pinnedCard.radius}px`,
      }}
      animate={{
        y: `${yPx}px`,
        x: `${xPx}px`,
        fontSize: `${fontSize}px`,
        width: `${dimensions.videoStage.unscaled.width}px`,
        height: `${height}px`,
        borderRadius: animateRadius,
        opacity: isCollapsedEmbeddedPlayer ? '0' : '1',
      }}
      exit={{ opacity: '0' }}
    >
      <div
        className='flex items-center'
        css={css`
          flex: 0 1 0%;
        `}
      >
        {!isSmall && hasTimer && endsIn && (
          <div
            id='ExpiresTimer'
            ref={refTimeDisplay}
            className='scale font-mono bg-black text-white rounded-lg whitespace-nowrap'
            css={css`
              padding: 0.25rem 1rem;
            `}
          ></div>
        )}
      </div>

      <div
        className='flex flex-1'
        css={css`
          color: ${readableColorOnPrimary};
          justify-content: center;
          padding: 0 0.5rem;
          padding-left: ${hasTimer ? '0.5rem' : '0'};
          min-width: 0;
        `}
      >
        <div
          className='scale'
          css={css`
            min-width: 0;
          `}
        >
          <FilledMarkdown
            md={interaction.details?.title ?? ''}
            css={css`
              min-width: 0;
              p {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            `}
          />
        </div>
      </div>
      <div>
        <CardMain.SubmitButton
          backClassName='width-auto'
          className={`scale min-h-0`}
          css={css`
            padding: ${isSmall ? '0.4rem 1rem' : '0.4rem 2rem'};
          `}
          onClick={(e): void => {
            e.preventDefault();
            onButtonClick();
          }}
        >
          {interaction.isSingleButtonCard() ? interaction.details?.buttonText ?? t`Go` : t`Go`}
        </CardMain.SubmitButton>
      </div>
    </motion.div>
  );
});

export const PinnedCardBar: React.FC = withWebinarPlayerContext([
  'pinnedInteraction',
  'currentRoom',
  'editMode',
])(({ context }) => {
  const { pinnedInteraction, currentRoom, editMode } = context;

  if (!pinnedInteraction || !currentRoom.caps.hasInteractions || editMode === 'dash.chat') {
    return null;
  }

  return (
    <AnimatePresence>
      <AttendeeCardForm key={pinnedInteraction.shortId} interaction={pinnedInteraction}>
        <PinnedCard interaction={pinnedInteraction} />
      </AttendeeCardForm>
    </AnimatePresence>
  );
});
